import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Bell, CheckCircle2, HandHeart, Clock3 } from "lucide-react";
import { Link } from "react-router-dom";

export default function Donate() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [donationsEnabled, setDonationsEnabled] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/donation-packages").then((r) => {
      setPackages(r.data.packages || []);
      setDonationsEnabled(!!r.data.donations_enabled);
    });
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name || "", email: user.email || "" }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Email required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/donation-interest", form);
      setSubmitted(true);
      toast.success("Thank you — we'll let you know the moment we open donations.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="donate-page" className="min-h-screen bg-[var(--bone)] text-[var(--ink)]">
      <Nav />

      <section className="pt-32 pb-12 md:pt-40">
        <div className="container-x">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="overline text-[var(--terracotta)]">Donate</span>
            <span className="inline-flex items-center gap-1.5 bg-[var(--terracotta)]/12 border border-[var(--terracotta)]/40 px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--terracotta)]">
              <Clock3 className="w-3 h-3" /> Not yet accepting donations
            </span>
          </div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-[-0.025em] leading-[0.95] max-w-5xl">
            We&apos;re putting the <em className="text-[var(--terracotta)] not-italic">final pieces</em> in place
            before opening donations.
          </h1>
          <p className="text-[var(--ink)]/75 mt-8 max-w-2xl leading-relaxed text-lg">
            The Irene Foundation is currently finalising legal registration, fiscal sponsorship,
            and our public financial reporting framework. We want every dollar to be transparent
            and accountable from day one — so we are not accepting donations just yet.
          </p>
          <p className="text-[var(--ink)]/75 mt-4 max-w-2xl leading-relaxed">
            Leave your email and we&apos;ll send you a personal note the moment our doors open. No
            spam, no third parties — just one launch announcement.
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-x grid grid-cols-12 gap-10 items-start">
          {/* Left: image + quote */}
          <div className="col-span-12 md:col-span-5">
            <div className="overflow-hidden">
              <img
                src="https://images.pexels.com/photos/7868829/pexels-photo-7868829.jpeg"
                alt=""
                className="w-full h-[440px] object-cover"
              />
            </div>
            <p className="text-sm text-[var(--muted)] mt-4 italic">
              &ldquo;She built her first circuit on a Tuesday. By Friday she was teaching the class.&rdquo; — Field report, Nigeria
            </p>

            <div className="mt-10 text-sm">
              <div className="border border-black/10 p-5">
                <div className="overline text-[var(--terracotta)]">In the meantime</div>
                <a href="/#volunteer" className="font-serif text-xl mt-2 block leading-tight hover:text-[var(--terracotta)]">Volunteer with us →</a>
              </div>
            </div>
          </div>

          {/* Right: notify form OR thank you */}
          <div className="col-span-12 md:col-span-7 md:pl-8">
            <div className="bg-[var(--forest)] text-[var(--bone)] p-10 md:p-14 relative overflow-hidden">
              <div className="grain absolute inset-0" />
              <div className="relative">
                {submitted ? (
                  <div data-testid="donate-thanks" className="py-6">
                    <CheckCircle2 className="w-10 h-10 text-[var(--terracotta)]" />
                    <h2 className="font-serif text-4xl mt-6 tracking-[-0.02em] leading-[1.05]">
                      You&apos;re on the list.
                    </h2>
                    <p className="text-[var(--bone)]/75 mt-4 max-w-md">
                      The next time you hear from us, donations will be open and you&apos;ll be among
                      the first to know. Thank you for believing in this work before we&apos;ve even started.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="donate-interest-form">
                    <div className="overline text-[var(--terracotta)] inline-flex items-center gap-2">
                      <Bell className="w-3.5 h-3.5" /> Get the launch announcement
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl mt-4 tracking-[-0.02em] leading-[1.05]">
                      Be the first to know when we open donations.
                    </h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <label className="block">
                        <span className="overline text-[var(--bone)]/60">Name (optional)</span>
                        <input data-testid="interest-name" className="editorial-input on-dark mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
                      </label>
                      <label className="block">
                        <span className="overline text-[var(--bone)]/60">Email</span>
                        <input data-testid="interest-email" type="email" required className="editorial-input on-dark mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                      </label>
                      <label className="block md:col-span-2">
                        <span className="overline text-[var(--bone)]/60">A short note (optional)</span>
                        <textarea data-testid="interest-note" rows={2} className="editorial-input on-dark mt-2 resize-none" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Anything you'd like us to know…" />
                      </label>
                    </div>
                    <button data-testid="donate-interest-submit" type="submit" disabled={submitting} className="btn-primary mt-10 disabled:opacity-60">
                      {submitting ? "Sending…" : "Notify me when donations open"} <ArrowRight className="w-4 h-4" />
                    </button>
                    <div className="text-xs text-[var(--bone)]/55 mt-4">
                      One email. No spam. We&apos;ll never share your details.
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preview of programs that donations will fund */}
      <section className="bg-[var(--bone-2)] py-24 md:py-32">
        <div className="container-x">
          <div className="grid grid-cols-12 gap-6 items-end mb-12">
            <div className="col-span-12 md:col-span-8">
              <div className="overline text-[var(--terracotta)]">When we launch</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 tracking-[-0.02em] leading-[1.05]">
                Here&apos;s what your support will make possible.
              </h2>
            </div>
            <p className="col-span-12 md:col-span-4 text-sm text-[var(--ink)]/70">
              A preview of the giving tiers we&apos;ll launch with — published now in the spirit of
              radical transparency.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {packages.map((p, i) => (
              <div key={p.id} data-testid={`preview-pkg-${p.id}`} className="bg-[var(--bone)] border border-black/10 p-7 relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">0{i + 1} / {p.label}</div>
                <div className="font-serif text-5xl mt-4 tracking-[-0.02em]">${p.amount}</div>
                <p className="text-sm text-[var(--ink)]/75 mt-3 leading-relaxed">{p.description}</p>
                <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--terracotta)]">
                  <HandHeart className="w-3 h-3" /> Soon
                </div>
              </div>
            ))}
          </div>
          {donationsEnabled && (
            <p className="text-sm text-[var(--terracotta)] mt-8 font-mono">
              Donations are now live — refresh to start contributing.
            </p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
